import { ShieldAlert, TriangleAlert } from 'lucide-react';
import { formatDate } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { RateText } from '@/components/data/RateText';
import { formatLanguages } from './api';
import type { CandidateListItem } from './types';

/** Board card: name, id, label, a few skills, rate and supplier, consent / erased flags. */
export function CandidateCard({ candidate }: { candidate: CandidateListItem }) {
  const shownSkills = candidate.skills.slice(0, 3);
  const more = candidate.skills.length - shownSkills.length;
  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium">{candidate.computed.displayName}</p>
          <p className="truncate font-mono text-xs text-muted-foreground">{candidate.candidateId}</p>
        </div>
        {candidate.erasedAt ? (
          <ShieldAlert className="h-4 w-4 shrink-0 text-destructive" aria-label="Erased" />
        ) : candidate.computed.consentExpiresSoon ? (
          <TriangleAlert
            className="h-4 w-4 shrink-0 text-warning"
            aria-label={`Consent expires ${formatDate(candidate.computed.consentExpiry)}`}
          />
        ) : null}
      </div>
      {candidate.label ? <p className="truncate text-xs text-muted-foreground">{candidate.label}</p> : null}
      {shownSkills.length > 0 ? (
        <ul className="flex flex-wrap gap-1" aria-label="Skills">
          {shownSkills.map((s) => (
            <li key={s}>
              <Badge variant="secondary" className="rounded-sm px-1 py-0 text-[11px] font-normal">
                {s}
              </Badge>
            </li>
          ))}
          {more > 0 ? <li className="text-[11px] leading-5 text-muted-foreground">+{more}</li> : null}
        </ul>
      ) : null}
      {candidate.languages.length > 0 ? (
        <p className="truncate text-xs text-muted-foreground">{formatLanguages(candidate.languages)}</p>
      ) : null}
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <RateText rate={candidate.rateExpectationEurH} className="text-foreground" />
        <span className="truncate">{candidate.supplier?.name ?? 'Direct'}</span>
      </div>
    </div>
  );
}
